import { useLocation } from "react-router-dom";
import { useDebounce } from "../../hooks/useDebounce";
import React, { useState, useEffect, useRef, useCallback } from "react";
import SectionHeader from "../../components/home/exploreBooksComponents/SectionHeader";
import CategoryFilter from "../../components/home/exploreBooksComponents/CategoryFilter";
import BookManager from "../../services/home/BookManager";
import BookGrid from "../../components/home/exploreBooksComponents/BookGrid";
import { useSelector } from "react-redux";
import Lottie from "lottie-react";
import exploreLoader from "../../loader/explore-loader.json";
import { motion } from "framer-motion";

const PAGE_SIZE = 30;
const PREFETCH_PAGES = 5; // number of pages ahead to prefetch
const MAX_CACHED_PAGES = 15; // max pages to keep in cache (sliding window)
const MIN_LOADER_MS = 1500; // show loader at least this long when requested

const ExploreBooks = () => {
  const location = useLocation();
  const categoryFilterRef = useRef(null);
  const observerRef = useRef();
  const userId = useSelector((state) => state.auth.user?.userId) || null;

  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 500);
  const [category, setCategory] = useState(location.state?.category || null);

  useEffect(() => {
    if (location.state?.category) {
      setCategory(location.state.category);
    }
  }, [location.state]);
  const [view, setView] = useState("grid");
  const [language, setLanguage] = useState("");
  const [books, setBooks] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false); // general fetch state
  const [showLoader, setShowLoader] = useState(false); // visible loader for next pages
  const [hasMore, setHasMore] = useState(true);
  const [firstPageReady, setFirstPageReady] = useState(false);

  // cache: Map<pageNumber, mappedBooks[]>
  const pageCacheRef = useRef(new Map());
  // track pages currently being fetched to avoid duplicate work
  const fetchingPagesRef = useRef(new Set());

  const loadPageRef = useRef();
const prefetchRef = useRef();

// token to identify the latest load operation (prevents race updates)
const latestLoadIdRef = useRef(0);

  // helper: map API response item to UI shape
  const mapBooks = (content) =>
    content.map((b) => ({
      id: b.bookId || b.id,
      title: b.bookName || b.title,
      author: b.author?.authorName || b.author,
      isbn: b.bookIsbn13 || b.isbn,
      description: b.bookDescription || " ",
      rating: b.averageRating,
      image: Array.isArray(b.bookImages) ? b.bookImages[0] : b.image,
      inStock: b.bookStockStatus,
    }));

    // wait for first N images to decode or timeout
const waitForFirstImagesToPaint = async (bookList = [], count = 4, timeout = 2000) => {
  const imgs = bookList.map((b) => b.image).filter(Boolean).slice(0, count);
  if (imgs.length === 0) return;

  const loadPromises = imgs.map((src) => {
    return new Promise((resolve) => {
      try {
        const img = new Image();
        img.src = src;
        if (img.decode) {
          img.decode().then(resolve).catch(resolve);
        } else {
          img.onload = resolve;
          img.onerror = resolve;
        }
      } catch {
        resolve();
      }
    });
  });

  await Promise.race([
    Promise.all(loadPromises),
    new Promise((res) => setTimeout(res, timeout)),
  ]);
};


  // preload images (using decode when possible)
  const preloadImages = async (bookList = []) => {
    const imgs = bookList.map((b) => b.image).filter(Boolean);
    if (imgs.length === 0) return;
    // limit concurrent preloads to avoid flooding network/decoding
    const CONCURRENT = 6;
    const queue = [...imgs];
    const runners = new Array(CONCURRENT).fill(null).map(async () => {
      while (queue.length) {
        const src = queue.shift();
        if (!src) break;
        try {
          const img = new Image();
          img.src = src;
          if (img.decode) {
            await img.decode().catch(() => {});
          } else {
            await new Promise((res) => {
              img.onload = res;
              img.onerror = res;
            });
          }
        } catch {
          // ignore
        }
      }
    });
    await Promise.all(runners);
  };

  // ensure a minimum time shown for loader (returns promise that resolves once min time elapsed)
  const ensureMinLoaderTime = async (startTs, minMs) => {
    const elapsed = Date.now() - startTs;
    if (elapsed < minMs) {
      await new Promise((r) => setTimeout(r, minMs - elapsed));
    }
  };

// --- put inside component, replace existing functions ---

// stable fetch that uses latest filters
const fetchPageFromApi = useCallback(
  async (pageNum) => {
    if (fetchingPagesRef.current.has(pageNum)) return null;
    fetchingPagesRef.current.add(pageNum);
    console.log("[fetchPageFromApi] page:", pageNum, "query:", debouncedQuery, "category:", category?.catId, "lang:", language);
    try {
      let data;
      let needsLanguageFilter = false;
      
      if (debouncedQuery.trim()) {
        const results = await BookManager.searchBooks(debouncedQuery.trim(), userId);
        data = { content: results, totalPages: 1, number: 0 };
        needsLanguageFilter = true; // Apply language filter on frontend for search
      } else if (category?.catId) {
        const results = await BookManager.getBooksByCategory(category.catId, userId);
        data = { content: results, totalPages: 1, number: 0 };
        needsLanguageFilter = true; // Apply language filter on frontend for category
      } else {
        data = await BookManager.getAllBooks({
          page: pageNum,
          size: PAGE_SIZE,
          userId,
          language,
        });
      }
      
      // Apply language filter on frontend if needed
      let content = data.content || [];
      if (needsLanguageFilter && language) {
        console.log("[Language Filter] Filtering by:", language);
        console.log("[Language Filter] Sample book before filter:", content[0]);
        content = content.filter(book => {
          const bookLang = book.bookLanguage || book.language || book.bookLang;
          console.log("[Language Filter] Book:", book.bookName, "Language:", bookLang);
          // If no language specified on book, include it (for backward compatibility)
          if (!bookLang) return true;
          return bookLang.toUpperCase() === language.toUpperCase();
        });
        console.log("[Language Filter] Filtered count:", content.length);
      }
      
      const mapped = mapBooks(content);
      return { mapped, totalPages: data.totalPages ?? 0 };
    } catch (err) {
      console.error("fetchPageFromApi error", err?.response?.data || err?.message || err);
      return null;
    } finally {
      fetchingPagesRef.current.delete(pageNum);
    }
  },
  [debouncedQuery, category, language, userId]
);


// prefetch uses up-to-date fetchPageFromApi and totalPages
const prefetchPages = useCallback(
  async (currentPage) => {
    const start = currentPage + 1;
    const end = Math.min(currentPage + PREFETCH_PAGES, Math.max(0, totalPages - 1));
    const tasks = [];
    for (let p = start; p <= end; p++) {
      if (pageCacheRef.current.has(p) || fetchingPagesRef.current.has(p)) continue;
      // push promises to tasks array so we can await or settle them
      tasks.push(
        (async (pageNum) => {
          const result = await fetchPageFromApi(pageNum);
          if (result && result.mapped) {
            pageCacheRef.current.set(pageNum, result.mapped);
            // background preload
            preloadImages(result.mapped).catch(() => {});
            // simple LRU-ish cleanup
            if (pageCacheRef.current.size > MAX_CACHED_PAGES) {
              const keys = Array.from(pageCacheRef.current.keys()).sort((a, b) => a - b);
              while (pageCacheRef.current.size > MAX_CACHED_PAGES) {
                const k = keys.shift();
                if (k !== undefined) pageCacheRef.current.delete(k);
              }
            }
          }
        })(p)
      );
    }
    await Promise.allSettled(tasks);
  },
  [fetchPageFromApi, totalPages]
);

// loadPage uses fetchPageFromApi + prefetchPages and honors latestLoadIdRef
// loadPage uses fetchPageFromApi + prefetchPages and honors latestLoadIdRef
const loadPage = useCallback(
  async (pageNum, append = false, showMinLoader = true) => {
    // bump and capture the load id first
    const loadId = ++latestLoadIdRef.current;
    console.log("[loadPage] start", { pageNum, append, loadId });

    const startTs = Date.now();
    setLoading(true);
    if (pageNum > 0) setShowLoader(true);

    try {
      // cached quick path
      if (pageCacheRef.current.has(pageNum)) {
        const mapped = pageCacheRef.current.get(pageNum);
        if (latestLoadIdRef.current !== loadId) return; // stale
        setBooks((prev) => (append ? [...prev, ...mapped] : mapped));
        if (pageNum === 0) setFirstPageReady(true);
        // background decode
        preloadImages(mapped).catch(() => {});
      } else {
        // fetch from API
        const result = await fetchPageFromApi(pageNum);
        if (!result || !result.mapped) {
          // nothing -> keep UI as-is
          if (latestLoadIdRef.current !== loadId) return;
        } else {
          // store cache
          pageCacheRef.current.set(pageNum, result.mapped);
          if (latestLoadIdRef.current !== loadId) return;
          setTotalPages(result.totalPages);
          // preload first few images quickly
          const quick = result.mapped.slice(0, 6).map((b) => b.image).filter(Boolean);
          quick.forEach((src) => { const img = new Image(); img.src = src; });

          setBooks((prev) => (append ? [...prev, ...result.mapped] : result.mapped));

          if (pageNum === 0) {
            try {
              await waitForFirstImagesToPaint(result.mapped, 4, 2000);
            } catch {}
            if (latestLoadIdRef.current === loadId) setFirstPageReady(true);
          }

          // background decode
          preloadImages(result.mapped).catch(() => {});
        }

        // cache maintenance
        if (pageCacheRef.current.size > MAX_CACHED_PAGES) {
          const keys = Array.from(pageCacheRef.current.keys()).sort((a, b) => a - b);
          while (pageCacheRef.current.size > MAX_CACHED_PAGES) {
            const k = keys.shift();
            if (k !== undefined) pageCacheRef.current.delete(k);
          }
        }
      }
    } catch (err) {
      console.error("loadPage error", err);
    } finally {
      if (pageNum > 0) {
        try {
          await ensureMinLoaderTime(startTs, MIN_LOADER_MS);
        } catch {}
      }
      if (latestLoadIdRef.current === loadId) {
        setShowLoader(false);
        setLoading(false);
      }
      // prefetch next pages (don't await)
      setTimeout(() => prefetchPages(pageNum).catch(() => {}), 80);
    }
  },
  [fetchPageFromApi, prefetchPages]
);

// keep refs pointing at the current callback impls
useEffect(() => {
  loadPageRef.current = loadPage;
  prefetchRef.current = prefetchPages;
}, [loadPage, prefetchPages]);

useEffect(() => {
  // invalidate any ongoing loads to avoid races
  latestLoadIdRef.current++;

  // clear caches & fetch state
  pageCacheRef.current.clear();
  fetchingPagesRef.current.clear();

  // reset UI states (optionally keep previous books until firstPageReady)
  // setBooks([]); // <-- you can keep this commented to preserve old items until new ready
  setPage(0);
  setHasMore(true);
  setTotalPages(0);
  setFirstPageReady(false);

  // call the latest loadPage and prefetch via refs (avoid including them in deps)
  (async () => {
    try {
      if (loadPageRef.current) {
        await loadPageRef.current(0, false, false);
      }
      // fire-and-forget prefetch
      if (prefetchRef.current) {
        prefetchRef.current(0).catch(() => {});
      }
    } catch (e) {
      console.error("initial load error", e);
    }
  })();
  // depends only on the filter inputs (and not on loadPage/prefetch function identities)
}, [debouncedQuery, category, language]);




  // Infinite scroll observer
  const lastBookRef = useCallback(
    (node) => {
      if (loading || showLoader) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && !loading && !showLoader) {
          // if more pages available
          if (totalPages === 0 || page + 1 < totalPages) {
            setPage((prev) => prev + 1);
          }
        }
      });
      if (node) observerRef.current.observe(node);
    },
    [loading, showLoader, page, totalPages]
  );

  // When page updates (user scrolled to next), show from cache if possible and load/prefetch further
  useEffect(() => {
    if (page === 0) return;
    // If cached, append instantly. loadPage will still ensure images are preloaded if not cached.
    const append = true;
    loadPage(page, append, true).catch(() => {});
  }, [page, loadPage]);

  // update hasMore whenever totalPages changes
  useEffect(() => {
    if (totalPages > 0) {
      setHasMore(page + 1 < totalPages);
    }
  }, [totalPages, page]);

  return (
    <motion.div
      className="min-h-screen bg-white"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <SectionHeader
        title="Explore Books"
        placeholder="Search by Title, Category, Author or ISBN"
        onSearch={(q) => setQuery(q)}
        language={language}
        onLanguageChange={setLanguage}
      />

      <div className="container mx-auto px-4 py-2">
<BookGrid
  books={books}
  view={view}
  onViewChange={setView}
  loading={!firstPageReady && page === 0} // simpler & accurate
  firstPageReady={firstPageReady}
  page={page}
  categoryFilter={
    <CategoryFilter
      ref={categoryFilterRef}
      selectedCategory={category}
      onSelect={(cat) => setCategory(cat)}
      language={language}
      onLanguageChange={setLanguage}
    />
  }
/>



       {!loading && (page > 0 || firstPageReady) && books.length === 0 && (
  <div className="mt-6 text-center text-gray-500">
    No books found for these filters.
  </div>
)}

        {books.length > 0 && hasMore && (
          <div ref={lastBookRef} className="h-1 w-full mt-5" />
        )}

        {/* Loader while fetching next pages */}
        {(showLoader || (loading && page > 0)) && (
          <div className="h-20 mt-6 flex justify-center items-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col items-center gap-2"
            >
              <Lottie
                animationData={exploreLoader}
                loop
                autoplay
                style={{ width: 80, height: 80 }}
              />
            </motion.div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ExploreBooks;
